// @flow
import React, { Component, Fragment } from 'react';
import { intlShape, defineMessages, FormattedMessage } from 'react-intl';
import classNames from 'classnames/bind';
import { Helmet } from 'react-helmet';
import PropTypes from 'prop-types';
import environment from '../../../environment';
import AlertContainer from '../../alert/container/AlertContainer';
import FreqQuestions from '../../../elements/FreqQuestions/FreqQuestions';
import { animateScroll as scroll, Element, scroller } from 'react-scroll/modules';
import Arrow from '../../../elements/Arrow/Arrow';
import NewFlightContent from './NewFlightContent/NewFlightContent';
import Loading from '../../../elements/Loading/Loading';
import Modal from '../../../elements/Modal/Modal';
import CarouselPressNew from '../../home/components/CarouselPressNew/CarouselPressNew';

import style from './NewFlight.scss';
import coverUSImg from '../../../assets/img/new_flight_meta_US_cover.png';
import coverPEImg from '../../../assets/img/new_flight_meta_PE_cover.png';
import coverCOImg from '../../../assets/img/new_flight_meta_CO_cover.png';
import icons from '../../../assets/styles/icons.scss';
import benefitOne from '../../../assets/img/new-flight/landing/benefits/benefit_one.png';
import benefitTwo from '../../../assets/img/new-flight/landing/benefits/benefit_two.png';
import benefitThree from '../../../assets/img/new-flight/landing/benefits/benefit_three.png';

import type { HTTPError } from '../../../error';
import type { User } from '../../../entities';
import type { LocaleState } from '../../locale/duck';
import type { LocalValuesState } from '../../local-values/duck';
import type { Question } from '../../../elements/FreqQuestions/FreqQuestions';

type Props = {
  +authenticated?: boolean;
  +user?: User;
  +locale: LocaleState;
  +localValues: LocalValuesState;
  +postFlight: (flightDate: string, departureCountry: string, departureAdmin1: number,
                departureAdmin2: number, arrivalCountry: string, arrivalAdmin1: number,
                arrivalAdmin2: number) => void;
  +isPosting?: boolean;
  +postError?: HTTPError;
  +handleOpenAuthModal: (openLoginView?: boolean) => void;
  +resetAlertErrorState: () => void;
  +authModalVisible?: boolean;
  +resetAlertState: () => void;
  +chosenVariants?: { [key: string]: number };
  +postPhone: (phone: string, country: string, enableSmsNotifications: boolean) => void;
};

type State = {
  greyLeftArrow: boolean;
  greyRightArrow: boolean;
  isBrowser: boolean;
};

export type TravelerStep = {
  title: string;
  text: string;
};

const travelerSteps: TravelerStep[] = [
  {
    title: 'newFlight.infoSteps.one.title',
    text: 'newFlight.infoSteps.one.content',
  },
  {
    title: 'newFlight.infoSteps.two.title',
    text: 'newFlight.infoSteps.two.content',
  },
  {
    title: 'newFlight.infoSteps.three.title',
    text: 'newFlight.infoSteps.three.content',
  }
];

const questionsIntlMap = {
  first: {
    title: 'reserve.faq.first.title',
    content: 'reserve.faq.first.content',
  },
  second: {
    title: 'reserve.faq.second.title',
    content: 'reserve.faq.second.content',
  },
  third: {
    title: 'reserve.faq.third.title',
    content: 'reserve.faq.third.content',
  },
  fourth: {
    title: 'reserve.faq.fourth.title',
    content: 'reserve.faq.fourth.content',
  },
  fifth: {
    title: 'reserve.faq.fifth.title',
    content: 'reserve.faq.fifth.content',
  },
};

const questions: Question[] = Object.keys(questionsIntlMap).map((key: string) => ({
  labelIntl: questionsIntlMap[key].title,
  contentIntl: questionsIntlMap[key].content,
}));

const messages = defineMessages({
  US: {
    title: {
      id: 'newFlight.document.title.US',
    },
    description: {
      id: 'newFlight.document.description.US',
    },
  },
  PE: {
    title: {
      id: 'newFlight.document.title.PE',
    },
    description: {
      id: 'newFlight.document.description.PE',
    },
  },
  BR: {
    title: {
      id: 'newFlight.document.title.BR',
    },
    description: {
      id: 'newFlight.document.description.BR',
    },
  },
  MX: {
    title: {
      id: 'newFlight.document.title.MX',
    },
    description: {
      id: 'newFlight.document.description.MX',
    },
  },
  CO: {
    title: {
      id: 'newFlight.document.title.CO',
    },
    description: {
      id: 'newFlight.document.description.CO',
    },
  },
  CL: {
    title: {
      id: 'newFlight.document.title.CL',
    },
    description: {
      id: 'newFlight.document.description.CL',
    },
  },
  default: {
    title: {
      id: 'newFlight.document.title',
    },
    description: {
      id: 'newFlight.document.description',
    },
  },
});

const benefits = [
  {
    img: benefitOne,
    id: 'newFlight.benefits.one'
  },
  {
    img: benefitTwo,
    id: 'newFlight.benefits.two'
  },
  {
    img: benefitThree,
    id: 'newFlight.benefits.three'
  },
];

const countriesImages = {
  US: coverUSImg,
  PE: coverPEImg,
  CO: coverCOImg,
};

const SCROLL_OPTIONS = {
  duration: 350,
  delay: 0,
  smooth: 'easeInOutQuart',
  offset: -70,
};

export default class NewFlight extends Component<Props, State> {
  state = {
    greyLeftArrow: false,
    greyRightArrow: true,
    isBrowser: false,
  };

  componentWillUnmount() {
    const { resetAlertState } = this.props;
    resetAlertState();
  }

  componentDidUpdate(prevProps: Props) {
    const { postError } = prevProps;
    if (!postError && this.props.postError) {
      scroll.scrollTo(0, {
        duration: 400,
      });
    }
  }

  componentDidMount() {
    this.setState({
      isBrowser: true,
    });
  }

  handleGoToRegisterFlight = () => {
    scroller.scrollTo('newFlight', SCROLL_OPTIONS);
  };

  buildBenefitsBoxes = (): Array<Element<'section'>> => {
    return benefits.map((benefit: { [string]: string }, index: number) => (
      <section key={index}>
        <article className={style.box}>
          <img src={benefit.img}
               alt="benefit"
               draggable="false"/>
          <FormattedMessage id={benefit.id}>{
            txt => <p className={style.subtitle}>{txt}</p>
          }</FormattedMessage>
        </article>
      </section>
    ));
  };

  handleGreyArrows(currentSlide: number) {
    const greyLeftArrow = currentSlide !== 0;
    const greyRightArrow = currentSlide !== benefits.length - 1;
    this.setState({ greyLeftArrow, greyRightArrow })
  }

  render() {
    const {
      locale, postFlight, isPosting, postError, handleOpenAuthModal,
      authenticated, user, authModalVisible, localValues, chosenVariants, postPhone,
    } = this.props;
    const { greyLeftArrow, greyRightArrow, isBrowser } = this.state;
    const description = this.context.intl.formatMessage((messages[locale.domainCountry] || messages.default).description);
    const title = this.context.intl.formatMessage((messages[locale.domainCountry] || messages.default).title);
    const baseUrl = environment.baseUrl[locale.domainCountry || 'US'];
    const coverImg = countriesImages[locale.domainCountry || 'US'];
    const settings = {
      infinite: false,
      slidesToScroll: 1,
      slidesToShow: 1,
      dots: true,
      arrows: true,
      draggable: true,
      dotsClass: classNames('slick-dots', style.dots),
      nextArrow: <Arrow icon={icons.iconChevronRightMedium}
                        positionClass={classNames(style.arrowRight, { [style.grey]: !greyRightArrow })}
                        visible={true}/>,
      prevArrow: <Arrow icon={icons.iconChevronLeftMedium}
                        positionClass={classNames(style.arrowLeft, { [style.grey]: !greyLeftArrow })}
                        visible={true}/>,
      afterChange: (current) => this.handleGreyArrows(current),
    };
    return (
      <Fragment>
        <Helmet meta={[
          { name: 'description', content: description },
          { property: 'og:image', content: `${baseUrl}${coverImg}` },
          { property: 'og:title', content: title },
          { property: 'og:description', content: description },
          { name: 'twitter:title', content: title },
          { name: 'twitter:twitter:image', content: `${baseUrl}${coverImg}` },
          { name: 'twitter:twitter:description', content: description },]}>
          <title>{title}</title>
        </Helmet>
        {/*<BannerStrip/>*/}
        <section className={style.errorWrap}>
          <article className={style.errorContent}>
            <AlertContainer showId="new-flight"/>
          </article>
        </section>
        <Modal handleClose={() => undefined}
               open={isPosting}
               transparent
               maskColor="white"
               showCloseButton={false}>
          <Loading type="circle"
                   className={style.loading}/>
        </Modal>
        <NewFlightContent authenticated={authenticated}
                          user={user}
                          locale={locale}
                          localValues={localValues}
                          postFlight={postFlight}
                          postPhone={postPhone}
                          isPosting={isPosting}
                          postError={postError}
                          handleOpenAuthModal={handleOpenAuthModal}
                          authModalVisible={authModalVisible}
                          travelerSteps={travelerSteps}
                          benefits={benefits}
                          isBrowser={isBrowser}/>
        <section className={style.pressWrap}>
          {isBrowser && <CarouselPressNew locale={locale}/>}
        </section>
        <section className={style.freqQuestionsWrap}>
          <FreqQuestions titleIntl="newOrder.summary.faq.title"
                         customClassName={style.faqCustomClass}
                         questions={questions}/>
        </section>
      </Fragment>);///Home
  }
}

NewFlight.propTypes = {
  authenticated: PropTypes.bool,
  user: PropTypes.object,
  locale: PropTypes.object.isRequired,
  localValues: PropTypes.object,
  postFlight: PropTypes.func.isRequired,
  isPosting: PropTypes.bool,
  postError: PropTypes.object,
  handleOpenAuthModal: PropTypes.func.isRequired,
  resetAlertErrorState: PropTypes.func.isRequired,
  authModalVisible: PropTypes.bool,
  resetAlertState: PropTypes.func.isRequired,
  chosenVariants: PropTypes.object,
  postPhone: PropTypes.func.isRequired,
};

NewFlight.contextTypes = {
  intl: intlShape,
};



// WEBPACK FOOTER //
// ./src/modules/new-flight/components/NewFlight.jsx